import {
  Button,
  Divider,
  Icons,
  MetadataChipV2,
  MetricsBox,
  Panel,
  tokens,
  Typography,
  Stack,
} from '@cast/design-system';
import { useOptimizeBannerMetrics } from '@cast/features-cluster-shared';
import { useIsOpsPilotEnabled } from '@cast/features-organization-shared-opspilot';
import {
  useAutoOptimization,
  useCanUseOptimizationWizard,
} from '@cast/one-click-features-opsPilot-optimizationWizard';
import { dashesOrNum } from '@cast/utils';

import { OPTIMIZE_BANNER_COPY } from '../_constants/optimizeBanner';
import { clusterDashboardChipSx } from '../_constants/dashboardTokens';
import { useOptimizeClusterNavigation } from '../_hooks/useOptimizeClusterNavigation';
import { BannerMetricPanelSkeleton } from './shared/BannerMetricPanelSkeleton';
import { BannerMetricTrendChip } from './shared/BannerMetricTrendChip';
import { ClusterBannerLayout } from './shared/ClusterBannerLayout';
import { OptimizeBannerSteps } from './shared/OptimizeBannerSteps';

const METRIC_PANEL_MIN_WIDTH = 184;

const unlockIconMap = {
  Repeat: Icons.Repeat,
  CloudArrowDown: Icons.CloudArrowDown,
  CurrencyDollarSimple: Icons.CurrencyDollarSimple,
} as const;

const formatPctValue = (value?: number) =>
  dashesOrNum({
    value,
    roundBy: 0,
    decimals: 0,
    mapDashes: () => '—',
  });

export const OptimizeBanner = () => {
  const { navigateToOptimize, isNavigationReady } =
    useOptimizeClusterNavigation();
  const { canViewAutopilot } = useCanUseOptimizationWizard();
  const { startOpsPilot } = useAutoOptimization();
  const { opsPilotStatus, onTryOpsPilot } = useIsOpsPilotEnabled();
  const {
    utilizationGainPct,
    recoverableSpendLabel,
    safeConsolidationCount,
    hasMetrics,
    isMetricsLoading,
  } = useOptimizeBannerMetrics();

  const copy = OPTIMIZE_BANNER_COPY;

  const canOpenWizard = canViewAutopilot && opsPilotStatus !== 'disabled';
  const handleOptimizeClick = () => {
    if (canOpenWizard) {
      ({ on: startOpsPilot, off: onTryOpsPilot })[opsPilotStatus]();
      return;
    }

    navigateToOptimize();
  };

  return (
    <ClusterBannerLayout
      testId="optimize-banner"
      main={
        <Stack flex={1} gap={16} p={24} minWidth={0}>
          <Stack gap={24}>
            <Stack gap={8}>
              <Typography
                variant="L12B"
                sx={{
                  color: tokens.theme.fg.structural.secondary,
                  fontWeight: 600,
                }}
              >
                {copy.label}
              </Typography>
              <Stack gap={4}>
                <Typography
                  variant="h5"
                  sx={{ color: tokens.theme.fg.structural.primary }}
                >
                  {copy.title}
                </Typography>
                <Typography
                  variant="P12R"
                  sx={{ color: tokens.theme.fg.structural.secondary }}
                >
                  {copy.description}
                </Typography>
              </Stack>
            </Stack>

            {isMetricsLoading ? (
              <Stack direction="row" gap={10} flexWrap="wrap">
                <BannerMetricPanelSkeleton minWidth={METRIC_PANEL_MIN_WIDTH} />
                <BannerMetricPanelSkeleton minWidth={METRIC_PANEL_MIN_WIDTH} />
                <BannerMetricPanelSkeleton minWidth={METRIC_PANEL_MIN_WIDTH} />
              </Stack>
            ) : hasMetrics ? (
              <Stack direction="row" gap={10} flexWrap="wrap">
                <Panel
                  sx={{
                    p: 16,
                    flex: `1 1 ${METRIC_PANEL_MIN_WIDTH}px`,
                    minWidth: METRIC_PANEL_MIN_WIDTH,
                  }}
                >
                  <MetricsBox
                    size="m"
                    slots={{
                      label: 'Utilization gain',
                      value: formatPctValue(utilizationGainPct),
                      unit: utilizationGainPct != null ? '%' : undefined,
                      footer:
                        utilizationGainPct != null ? (
                          <BannerMetricTrendChip
                            direction="up"
                            label={`${formatPctValue(utilizationGainPct)}%`}
                          />
                        ) : undefined,
                    }}
                  />
                </Panel>
                <Panel
                  sx={{
                    p: 16,
                    flex: `1 1 ${METRIC_PANEL_MIN_WIDTH}px`,
                    minWidth: METRIC_PANEL_MIN_WIDTH,
                  }}
                >
                  <MetricsBox
                    size="m"
                    slots={{
                      label: 'Recoverable spend',
                      value: recoverableSpendLabel,
                      footer:
                        recoverableSpendLabel !== '—' ? (
                          <BannerMetricTrendChip
                            direction="down"
                            label={recoverableSpendLabel}
                          />
                        ) : undefined,
                    }}
                  />
                </Panel>
                <Panel
                  sx={{
                    p: 16,
                    flex: `1 1 ${METRIC_PANEL_MIN_WIDTH}px`,
                    minWidth: METRIC_PANEL_MIN_WIDTH,
                  }}
                >
                  <MetricsBox
                    size="m"
                    slots={{
                      label: 'Safe consolidation',
                      value: formatPctValue(safeConsolidationCount),
                      unit:
                        safeConsolidationCount != null
                          ? 'workloads'
                          : undefined,
                      footer:
                        safeConsolidationCount != null ? (
                          <BannerMetricTrendChip
                            direction="up"
                            label={`${formatPctValue(safeConsolidationCount)}`}
                          />
                        ) : undefined,
                    }}
                  />
                </Panel>
              </Stack>
            ) : null}

            <Stack gap={4}>
              <Typography
                variant="P12M"
                sx={{ color: tokens.theme.fg.structural.secondary }}
              >
                {copy.installPrefix}
              </Typography>
              <Stack direction="row" gap={8} flexWrap="wrap">
                {copy.unlockFeatures.map((feature) => {
                  const Icon = unlockIconMap[feature.icon];
                  return (
                    <MetadataChipV2
                      key={feature.label}
                      label={feature.label}
                      size="m"
                      variant="subtle"
                      slots={{ startIcon: <Icon size={12} /> }}
                      sx={clusterDashboardChipSx.metadataLabel}
                    />
                  );
                })}
              </Stack>
            </Stack>
          </Stack>

          <Divider />

          <Button
            variant="primary"
            size="md"
            onClick={handleOptimizeClick}
            disabled={!canOpenWizard && !isNavigationReady}
            testId="optimize-cluster-button"
          >
            {copy.cta}
          </Button>
        </Stack>
      }
      sidePanel={
        <OptimizeBannerSteps label={copy.sidePanelLabel} steps={copy.steps} />
      }
    />
  );
};
